import { Marker } from '@react-google-maps/api';
import { MapLocationData } from '@hero/web-components';
import { useMapMarkerColors } from '@hero/web-hooks';
import { useMapContext } from '@hero/web-context';

export interface DefaultLocationMarkerProps {
  /** Location data */
  location: MapLocationData;
  /** Index of the location */
  index: number;
  /** Callback when marker is clicked */
  onClick?: () => void;
  /** Optional distance information */
  distance?: {
    text: string;
    value: number;
  };
  /** Optional duration information */
  duration?: {
    text: string;
    value: number;
  };
  /** Whether marker is draggable */
  draggable?: boolean;
  /** Callback when marker is dragged */
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
}

/**
 * DefaultLocationMarker - Custom styled default location marker with MapPin icon
 * Follows the same structure as PickupMarker and DropoffMarker for consistency
 * Uses Marker component with custom SVG icon for draggable functionality
 * Note: This marker is hidden when editing (PendingLocationMarker is shown instead)
 */
export function DefaultLocationMarker({
  location,
  index,
  onClick,
  distance,
  duration,
  draggable = false,
  onDragEnd,
}: DefaultLocationMarkerProps) {
  const { markerColors } = useMapMarkerColors();
  const ctx = useMapContext();

  // Determine if location is inaccurate and indicator is enabled
  const isInaccurate = location.isAccurateLocation === false && ctx.enableAccurateLocationIndicator;

  // Use colors from centralized hook
  const fillColor = isInaccurate
    ? markerColors.notAccurateLocation.fillColor
    : markerColors.default.fillColor;
  const pulseColor = isInaccurate
    ? markerColors.notAccurateLocation.paluse
    : markerColors.default.paluse;
  const strokeColor = isInaccurate ? markerColors.notAccurateLocation.fillColor : '#ffffff';

  // Generate custom SVG icon similar to PendingLocationMarker but with MapPin icon
  const iconUrl =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <!-- Shadow matching Tailwind shadow-lg -->
        <filter id="markerShadow${index}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
          <feOffset dx="0" dy="5" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.1"/>
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="2"/>
          <feOffset dx="0" dy="2" result="offsetblur2"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.1"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <!-- Outer pulse circle (animated) - 40px circle -->
      <circle cx="20" cy="20" r="20" fill="${pulseColor}" opacity="0.2">
        <animate attributeName="r" values="20;24;20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.2;0;0.2" dur="2s" repeatCount="indefinite"/>
      </circle>
      <!-- Main marker circle - 24px circle (12px radius), background color, border-2, and shadow-lg -->
      <g filter="url(#markerShadow${index})">
        <circle cx="20" cy="20" r="12" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2"/>
        <!-- MapPin icon - centered, white color -->
        <g transform="translate(20, 20)">
          <path d="M0 -7 C-2.5 -7 -4 -4.5 -4 -2 C-4 0.5 0 5 0 5 C0 5 4 0.5 4 -2 C4 -4.5 2.5 -7 0 -7 Z" fill="#ffffff"/>
          <circle cx="0" cy="-2" r="2" fill="${fillColor}"/>
        </g>
      </g>
    </svg>
  `);

  return (
    <>
      <Marker
        position={location.cords}
        draggable={draggable}
        clickable={true}
        icon={{
          url: iconUrl,
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20),
        }}
        onClick={onClick}
        onDragEnd={onDragEnd}
        title={location.fullAddress || `Location ${index + 1}`}
      />

      {/* Circle for inaccurate locations */}
      {/* {location.isAccurateLocation === false && (
        <Circle
          center={location.cords}
          radius={100}
          options={{
            fillColor: '#EF4444',
            fillOpacity: 0.1,
            strokeColor: '#EF4444',
            strokeWeight: 2,
            strokeOpacity: 0.5,
          }}
        />
      )} */}
    </>
  );
}
